import { z } from 'zod';
import { CONSENT_TYPES, EVENT_STATUSES, INFORMATION_REQUEST_TYPES, REQUESTABLE_ITEM_TYPES, USER_ROLES, } from './vocabulary.js';
import { LANGUAGE_CODE_PATTERN } from './language.js';
import { isPlausibleEmail } from './email.js';
/* -------------------------------------------------------------------------- */
/* Primitive                                                                  */
/* -------------------------------------------------------------------------- */
export const uuidSchema = z.string().uuid();
export const languageCodeSchema = z
    .string()
    .regex(LANGUAGE_CODE_PATTERN, 'Codice lingua non valido.');
export const emailSchema = z
    .string()
    .trim()
    .max(254)
    .refine(isPlausibleEmail, { message: 'Indirizzo e-mail non valido.' });
/** Versione di un testo pubblicato: `privacy-2026-09-v3`, `marketing-it-v2`. */
export const versionSchema = z.string().trim().min(1).max(120);
/* -------------------------------------------------------------------------- */
/* Riferimenti immutabili alla documentazione mostrata (§8, §21, §22)          */
/* -------------------------------------------------------------------------- */
/**
 * Identità di ciò che il visitatore ha effettivamente visto.
 * Versione e lingua viaggiano sempre insieme: una versione senza lingua non
 * identifica un testo (§21).
 */
export const policyReferenceSchema = z.object({
    policyId: uuidSchema,
    policyVersion: versionSchema,
    policyLanguage: languageCodeSchema,
});
export const consentTextReferenceSchema = z.object({
    consentTextId: uuidSchema,
    consentTextVersion: versionSchema,
    consentTextLanguage: languageCodeSchema,
});
/* -------------------------------------------------------------------------- */
/* Consenso nel payload kiosk (§2.3, §6.6, §32)                               */
/* -------------------------------------------------------------------------- */
/**
 * Il consenso marketing è modellato come unione discriminata, non come boolean.
 *
 * È deliberato: rende **strutturalmente impossibile** inviare `granted: true`
 * senza la versione e la lingua del testo effettivamente mostrato. L'invariante
 * di §32 ("se il consenso è prestato, policy version e consent text version
 * sono registrate") è così garantita dal parsing, prima ancora che dai test.
 *
 * Assenza di consenso significa `granted: false`, mai un campo mancante:
 * il client deve dichiarare esplicitamente la scelta del visitatore.
 */
export const marketingConsentSchema = z.discriminatedUnion('granted', [
    z.object({ granted: z.literal(false) }),
    z.object({ granted: z.literal(true) }).merge(consentTextReferenceSchema),
]);
/* -------------------------------------------------------------------------- */
/* Sottomissione dal kiosk (§7, §9.3)                                         */
/* -------------------------------------------------------------------------- */
/**
 * Dati di contatto: solo i campi con una finalità documentata (§12).
 *
 * Non aggiungere telefono, indirizzo, ruolo, data di nascita o note libere
 * senza una decisione documentata del Titolare: il form è già il punto di
 * minimizzazione.
 */
export const contactInputSchema = z.object({
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    company: z.string().trim().max(200).optional(),
    email: emailSchema,
});
export const informationRequestTypeSchema = z.enum(INFORMATION_REQUEST_TYPES);
export const kioskSubmissionSchema = z.object({
    /**
     * UUID generato client-side: è la chiave di idempotenza del sync (§9.3).
     * Lo stesso `submissionId` che arriva due volte non crea un duplicato.
     */
    submissionId: uuidSchema,
    eventId: uuidSchema,
    contact: contactInputSchema,
    /** Cosa ha chiesto il visitatore. Almeno una richiesta: senza, non c'è finalità. */
    requestedItems: z
        .array(z.object({
        type: informationRequestTypeSchema,
        resourceId: z.string().trim().max(120).optional(),
    }))
        .min(1)
        .max(20),
    /**
     * Richiesta di ricontatto commerciale (§2.2).
     * È una richiesta dell'interessato, NON un consenso marketing: resta separata
     * e non deve mai essere derivata dall'una all'altra.
     */
    callbackRequested: z.boolean(),
    /** Consenso marketing, facoltativo e indipendente da tutto il resto (§2.3). */
    marketingConsent: marketingConsentSchema,
    /** Informativa effettivamente mostrata prima dell'invio (§8). */
    policy: policyReferenceSchema,
    /** Lingua in cui il visitatore ha compilato il form. */
    language: languageCodeSchema,
    /** Timestamp client: indicativo. Il server registra il proprio (§22). */
    submittedAt: z.string().datetime(),
});
/** Il sync accetta un lotto: la coda offline può accumulare più record (§9.2). */
export const kioskSyncRequestSchema = z.object({
    submissions: z.array(kioskSubmissionSchema).min(1).max(50),
});
export const kioskSyncResultSchema = z.object({
    results: z.array(z.object({
        submissionId: uuidSchema,
        /** `DUPLICATE` è un successo: il record era già stato accettato (§9.3). */
        status: z.enum(['ACCEPTED', 'DUPLICATE', 'REJECTED']),
        /** Messaggio privacy-safe, mai contenente dati personali (§26). */
        message: z.string().optional(),
    })),
});
/* -------------------------------------------------------------------------- */
/* Autenticazione (§10)                                                       */
/* -------------------------------------------------------------------------- */
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: z.string().min(1).max(512),
});
export const currentUserSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    role: z.enum(USER_ROLES),
});
/* -------------------------------------------------------------------------- */
/* Eventi / fiere (§6.2, §19)                                                 */
/* -------------------------------------------------------------------------- */
export const eventBrandingSchema = z.object({
    imageAssetId: uuidSchema.optional(),
    /** Obbligatorio in presenza di immagine: accessibilità (§20.6). */
    imageAltText: z.string().trim().max(300).optional(),
    headline: z.string().trim().max(200).optional(),
    shortDescription: z.string().trim().max(600).optional(),
});
/**
 * Voce richiedibile, configurabile per fiera (§19).
 *
 * Personalizza **che cosa si può chiedere**, mai a che cosa si acconsente: il
 * consenso marketing resta legato al proprio testo pubblicato e il ricontatto
 * commerciale alla propria casella. Ogni voce mappa su un `type` del
 * vocabolario esistente, quindi su una finalità già configurata: due voci
 * diverse dello stesso tipo — un whitepaper e un catalogo — restano un solo
 * trattamento, con un solo periodo di conservazione.
 *
 * Lo schema è `strict()` di proposito. Non esiste un campo per preselezionare
 * una voce e non deve poter esistere: se la configurazione ne contenesse uno,
 * verrebbe rifiutata invece di essere ignorata in silenzio. È il modo per
 * rendere impossibile — non solo sconsigliato — che una fiera spunti qualcosa
 * al posto del visitatore (§23).
 */
export const requestableItemSchema = z
    .object({
    /**
     * Identificatore della voce, stabile nel tempo: finisce in `resourceId`
     * sulla richiesta ed è ciò che documenta quale materiale è stato chiesto.
     */
    id: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Identificatore non valido.'),
    type: z.enum(REQUESTABLE_ITEM_TYPES),
    /**
     * Etichetta per lingua. Una voce priva di etichetta nella lingua servita
     * non viene offerta, per la stessa ragione per cui non si mostra
     * un'informativa in una lingua che il visitatore non ha scelto (§21).
     */
    labels: z.record(z.string().regex(LANGUAGE_CODE_PATTERN, 'Codice lingua non valido.'), z.string().trim().min(1).max(160)),
})
    .strict();
const requestableItemsSchema = z.array(requestableItemSchema).min(1).max(20);
/**
 * Etichetta della casella di ricontatto commerciale, per lingua (§2.2, §19).
 *
 * Personalizzabile per fiera perché il commerciale possa formularla come serve
 * al proprio pubblico, senza dover ricorrere a una voce dell'elenco che imiti
 * il ricontatto ricadendo però su un'altra finalità.
 *
 * Quello che la personalizzazione NON cambia è la finalità: comunque sia
 * scritta, la spunta produce una richiesta `COMMERCIAL_CALLBACK`, con il suo
 * periodo di conservazione e il suo export. È la differenza fra cambiare
 * un'etichetta e aprire una seconda via verso una finalità diversa.
 *
 * Vincoli sul testo, che il codice non può verificare ma che l'interfaccia
 * deve dichiarare: deve descrivere un ricontatto da parte del personale
 * commerciale — è ciò che l'informativa pubblicata dichiara — e non deve
 * assomigliare a un consenso marketing, che è una casella separata con
 * un'altra base giuridica.
 *
 * `null` riporta all'etichetta predefinita dell'applicazione.
 */
export const callbackLabelsSchema = z
    .record(z.string().regex(LANGUAGE_CODE_PATTERN, 'Codice lingua non valido.'), z.string().trim().min(1).max(200))
    .nullable();
/** Identificatori duplicati renderebbero ambiguo che cosa è stato richiesto. */
function assertUniqueItemIds(items, ctx) {
    const seen = new Set();
    for (const item of items) {
        if (seen.has(item.id)) {
            ctx.addIssue({
                code: 'custom',
                message: `Voce richiedibile duplicata: ${item.id}.`,
                path: ['requestableItems'],
            });
        }
        seen.add(item.id);
    }
}
/**
 * Ogni voce deve esistere almeno nella lingua predefinita della fiera.
 *
 * Senza questo vincolo una voce potrebbe non comparire in nessuna lingua e la
 * configurazione sembrerebbe applicata senza esserlo.
 */
function assertDefaultLanguageLabels(items, languages, ctx) {
    const fallback = languages[0];
    if (!fallback)
        return;
    for (const item of items) {
        if (!item.labels[fallback]) {
            ctx.addIssue({
                code: 'custom',
                message: `La voce "${item.id}" non ha etichetta nella lingua predefinita (${fallback}).`,
                path: ['requestableItems'],
            });
        }
    }
}
export const createEventSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    slug: z
        .string()
        .trim()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Slug non valido.'),
    location: z.string().trim().max(200).optional(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    /** Lingue offerte al visitatore per questa fiera (§19, §21). */
    languages: z.array(languageCodeSchema).min(1),
    branding: eventBrandingSchema.optional(),
    /** Omesso, la fiera eredita l'elenco predefinito applicato dalla migrazione. */
    requestableItems: requestableItemsSchema.optional(),
    callbackLabels: callbackLabelsSchema.optional(),
})
    .refine((e) => new Date(e.endsAt) >= new Date(e.startsAt), {
    message: 'La data di fine non può precedere quella di inizio.',
    path: ['endsAt'],
})
    .refine((e) => !e.branding?.imageAssetId || Boolean(e.branding.imageAltText), {
    message: "Un'immagine evento richiede un alt text (accessibilità, §20.6).",
    path: ['branding', 'imageAltText'],
})
    .superRefine((e, ctx) => {
    if (!e.requestableItems)
        return;
    assertUniqueItemIds(e.requestableItems, ctx);
    assertDefaultLanguageLabels(e.requestableItems, e.languages, ctx);
});
export const updateEventSchema = z
    .object({
    name: z.string().trim().min(1).max(200).optional(),
    location: z.string().trim().max(200).optional(),
    status: z.enum(EVENT_STATUSES).optional(),
    activePrivacyPolicyId: uuidSchema.optional(),
    languages: z.array(languageCodeSchema).min(1).optional(),
    branding: eventBrandingSchema.optional(),
    requestableItems: requestableItemsSchema.optional(),
    callbackLabels: callbackLabelsSchema.optional(),
})
    .superRefine((e, ctx) => {
    if (!e.requestableItems)
        return;
    assertUniqueItemIds(e.requestableItems, ctx);
    // La copertura della lingua predefinita si verifica nel servizio, dove è
    // nota anche la lingua della fiera già salvata: qui `languages` può mancare.
    if (e.languages)
        assertDefaultLanguageLabels(e.requestableItems, e.languages, ctx);
});
/* -------------------------------------------------------------------------- */
/* Informative e testi di consenso (§6.7, §6.8, §8)                           */
/* -------------------------------------------------------------------------- */
/**
 * Creazione di una bozza di informativa.
 *
 * `version` e `language` insieme identificano il testo: la stessa versione può
 * esistere in più lingue, ma una coppia (versione, lingua) è unica e, una volta
 * pubblicata, immutabile (§6.7, §21).
 *
 * Il contenuto è deciso dal Titolare/DPO: l'applicazione non lo genera né lo
 * suggerisce, si limita a versionarlo e a legarlo alle registrazioni.
 */
export const createPrivacyPolicySchema = z.object({
    version: versionSchema,
    language: languageCodeSchema,
    title: z.string().trim().min(1).max(300),
    content: z.string().min(1).max(200_000),
});
/**
 * Creazione di una bozza di testo di consenso.
 *
 * Separato dall'informativa (§6.8): il testo che il visitatore spunta ha una
 * propria versione, perché è quello che deve essere possibile esibire come
 * prova di che cosa esattamente sia stato accettato.
 */
export const createConsentTextSchema = z.object({
    type: z.enum(CONSENT_TYPES),
    version: versionSchema,
    language: languageCodeSchema,
    text: z.string().trim().min(1).max(5_000),
});
/**
 * Modifica di una bozza di informativa.
 *
 * Applicabile **solo** a una bozza: una versione pubblicata è immutabile e va
 * sostituita pubblicandone una nuova (§6.7, §35.4). Versione e lingua non sono
 * modificabili nemmeno in bozza — insieme identificano il documento, e
 * cambiarle equivarrebbe a crearne un altro.
 */
export const updatePrivacyPolicySchema = z.object({
    title: z.string().trim().min(1).max(300).optional(),
    content: z.string().min(1).max(200_000).optional(),
});
export const updateConsentTextSchema = z.object({
    text: z.string().trim().min(1).max(5_000),
});
/** Vista pubblica di un'informativa pubblicata, come la riceve il kiosk. */
export const publishedPolicySchema = z.object({
    policyId: uuidSchema,
    version: versionSchema,
    language: languageCodeSchema,
    title: z.string(),
    content: z.string(),
    hash: z.string(),
    publishedAt: z.string().datetime(),
});
export const publishedConsentTextSchema = z.object({
    consentTextId: uuidSchema,
    type: z.enum(CONSENT_TYPES),
    version: versionSchema,
    language: languageCodeSchema,
    text: z.string(),
    hash: z.string(),
});
/* -------------------------------------------------------------------------- */
/* Revoca del consenso (§14.2)                                                */
/* -------------------------------------------------------------------------- */
export const withdrawConsentSchema = z.object({
    consentType: z.enum(CONSENT_TYPES),
    /**
     * Motivazione obbligatoria per la tracciabilità dell'azione amministrativa.
     * Testo libero volutamente breve e non destinato a contenere dati personali.
     */
    reason: z.string().trim().min(1).max(500),
});
//# sourceMappingURL=schemas.js.map